import {
    MenuCompositeData,
    MenuExtraGroupData,
    MenuExtraProductData,
    MenuItemData,
    MenuPlgData,
    MenuPlgSellableData,
    MenuSellableData
} from '../menu/menu-entity.model';
import {BehaviorSubject, debounceTime, Subject} from "rxjs";
import {uuid} from '../utils/string-utils';
import {OrderModel} from './order.model';
import {MenuModel} from '../menu/menu.model';
import {computed, Signal, signal, WritableSignal} from '@angular/core';
import {MessageDetails, MessageLevel} from '../notification/message/message';

export enum OrderType {
    NONE,
    ORDER_AHEAD,
    ORDER_DELIVERY,
    ORDER_AT_TABLE
}

export namespace OrderType {
    export function toString(orderType: OrderType): string {
        return orderType === OrderType.ORDER_AT_TABLE ? "APP_AT_TABLE"
            : orderType === OrderType.ORDER_AHEAD ? "TAKEAWAY"
                : orderType === OrderType.ORDER_DELIVERY ? "DELIVERY"
                    : undefined;
    }

    export function parse(orderType: string): OrderType {
        return orderType === "APP_AT_TABLE" ? OrderType.ORDER_AT_TABLE
            : orderType === "TAKEAWAY" ? OrderType.ORDER_AHEAD
                : orderType === "DELIVERY" ? OrderType.ORDER_DELIVERY
                    : undefined;
    }
}

export interface DisplayableOrderExtraItem {
    nameSignal: () => string;
    groupNameSignal: () => string;
    quantitySignal: () => number;
    unitPriceSignal: () => number;
    totalSignal: () => number;
    isGroupSignal: () => boolean;
    isVisibleSignal: () => boolean;
    isNestedSignal: () => boolean;
}

export interface DisplayableOrderItem {
    readonly identifier: string;
    nameSignal: () => string;
    quantitySignal: () => number;
    unitPriceSignal: () => number;
    localTotalSignal: () => number;
    dealIdSignal: () => string;
    itemNotesSignal: () => string;
    initiatedTotalSignal: () => number;
    initiatedExtrasTotalSignal: () => number;
    orderedExtrasSignal: () => DisplayableOrderExtraItem[];
    compositeComponentsSignal: () => DisplayableOrderItem[];
}

export abstract class QuantifiedItem {
    protected _quantitySignal: WritableSignal<number> = signal(undefined);
    public quantitySignal: Signal<number> = this._quantitySignal.asReadonly();
    protected _debouncedQuantitySignal: WritableSignal<number> = signal(undefined);
    public debouncedQuantitySignal: Signal<number> = this._debouncedQuantitySignal.asReadonly();
    private quantityChanged$: Subject<number>;

    public get quantity(): number {
        return this._quantitySignal();
    }

    public set quantity(quantity: number) {
        this._quantitySignal.set(quantity);

        // Debounce notification of a quantity change
        // Subject is created on demand and completes after emission to prevent memory leaks
        if (!this.quantityChanged$) {
            this.quantityChanged$ = new BehaviorSubject<number>(1);
            this.quantityChanged$.pipe(debounceTime(300)).subscribe({
                next: () => {
                    this._debouncedQuantitySignal.set(this._quantitySignal());
                    OrderModel.instance.notifyOrderChange();
                    this.quantityChanged$.complete();
                    this.quantityChanged$.complete();
                    this.quantityChanged$ = undefined;
                }
            });
        }
        this.quantityChanged$.next(this.quantity);
    }

    /**
     * Increment the quantity
     * @param qty quantity to be incremented or if is undefined it will increment by 1
     */
    public incrementQuantity(qty?: number): void {
        this.quantity = this.quantity + (qty ? qty : 1);
    }

    /**
     * Decrement the quantity
     * @param qty quantity to be decremented or if is undefined it will decrement by 1
     */
    public decrementQuantity(qty?: number): void {
        let newQuantity = this.quantity - (qty ? qty : 1);
        if (newQuantity >= 0) {
            this.quantity = newQuantity;
        }
    }
}

export class OrderExtraItem extends QuantifiedItem implements DisplayableOrderExtraItem {

    public readonly identifier: string = uuid();

    public readonly nameSignal: Signal<string> = computed(() => MenuModel.instance.getExtraProductDisplayName(this.extraProduct));
    public readonly groupNameSignal: Signal<string> = computed(() => this.extraGroup.name);
    public readonly unitPriceSignal: Signal<number> = computed(() => this.extraProduct.price);
    public readonly totalSignal: Signal<number> = computed(() => this._quantitySignal() * this.unitPriceSignal());
    public readonly isGroupSignal: Signal<boolean> = computed(() => MenuModel.instance.extraGroupIsSingleSelect(this.extraGroup));
    public readonly isVisibleSignal: Signal<boolean> = computed(() => MenuModel.instance.extraGroupIsVisible(this.extraGroup));
    public readonly isNestedSignal: Signal<boolean> = computed(() => !!MenuModel.instance.getParentExtraGroupId(this.extraGroup, this.parent.sellable()));

    public sortingExtraGroupIndex: number;

    constructor(public readonly parent: OrderItem,
                public readonly extraGroup: MenuExtraGroupData,
                public readonly extraProduct: MenuExtraProductData,
                quantity: number = 1) {
        super();
        this._quantitySignal.set(quantity);
        this._debouncedQuantitySignal.set(quantity);
    }

    public createClone(orderItem: OrderItem): OrderExtraItem {
        return new OrderExtraItem(orderItem, this.extraGroup, this.extraProduct, this._quantitySignal());
    }
}

export class OrderItem extends QuantifiedItem implements DisplayableOrderItem {

    public readonly identifier: string = uuid();

    public readonly nameSignal: WritableSignal<string> = signal<string>(undefined);
    public readonly dealIdSignal: WritableSignal<string> = signal<string>(undefined);
    public readonly itemNotesSignal: WritableSignal<string> = signal<string>(undefined);
    public readonly initiatedTotalSignal: WritableSignal<number> = signal<number>(undefined);
    public readonly initiatedExtrasTotalSignal: WritableSignal<number> = signal<number>(undefined);
    public readonly orderedExtrasSignal: WritableSignal<OrderExtraItem[]> = signal<OrderExtraItem[]>([]);
    public readonly compositeComponentsSignal: Signal<DisplayableOrderItem[]> = signal<DisplayableOrderItem[]>(undefined).asReadonly();
    public readonly justOrdered: WritableSignal<boolean> = signal<boolean>(false);
    public readonly proposed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly added: WritableSignal<boolean> = signal<boolean>(false);

    protected readonly _menuItem: WritableSignal<MenuItemData> = signal<MenuItemData>(undefined);
    public readonly menuItem: Signal<MenuItemData> = this._menuItem.asReadonly();
    protected readonly _sellable: WritableSignal<MenuSellableData> = signal<MenuSellableData>(undefined);
    public readonly sellable: Signal<MenuSellableData> = this._sellable.asReadonly();

    public readonly compositeItemName: Signal<string> = computed(() => MenuModel.instance.getMenuItemDisplayName(this._sellable()?.menuItem) || this.nameSignal());

    public readonly unitPriceSignal: Signal<number> = computed(() => this._sellable()?.price);

    protected readonly _localTotalSignal: Signal<number> = computed(() => (this._quantitySignal() * this.unitPriceSignal()) + this._totalExtras());
    public readonly localTotalSignal: Signal<number> = computed(() => this._localTotalSignal());
    protected readonly _debouncedLocalTotalSignal: Signal<number> = computed(() => (this._debouncedQuantitySignal() * this.unitPriceSignal()) + this._totalExtras());
    public readonly debouncedLocalTotalSignal: Signal<number> = computed(() => this._debouncedLocalTotalSignal());

    public readonly _totalExtras: Signal<number> = computed(() => {
        let totalIncluded: number = this.totalIncludedSingleSelectExtras() + this.totalIncludedMultiSelectExtras();
        return this._quantitySignal() * this.orderedExtrasSignal().reduce((total: number, orderExtraItem: OrderExtraItem) => {
            let totalExtra: number = orderExtraItem.totalSignal();
            if (totalIncluded && orderExtraItem.extraGroup.totalIncluded) {
                if (totalIncluded >= totalExtra) {
                    totalIncluded -= totalExtra;
                    totalExtra = 0;
                } else {
                    totalExtra -= totalIncluded;
                    totalIncluded = 0;
                }
            }
            return total + totalExtra;
        }, 0);
    });
    public readonly totalExtras: Signal<number> = computed(() => this._totalExtras());

    public totalIncludedSingleSelectExtras: Signal<number> = computed(() =>
        this.availableSingleSelectExtraGroups().reduce((total: number, extraGroup: MenuExtraGroupData) => total + (extraGroup.totalIncluded || 0), 0));
    public totalIncludedMultiSelectExtras: Signal<number> = computed(() =>
        this.availableMultiSelectExtraGroups().reduce((total: number, extraGroup: MenuExtraGroupData) => total + (extraGroup.totalIncluded || 0), 0));

    /**
     * The group base price for the extras.
     * It can be the lowest price when there's no default qty or the price for the default qty.
     */
    public groupBasePriceMap: Signal<Map<number, number>> = computed(() => {
        const map: Map<number, number> = new Map();
        if (!this._sellable()) {
            return map;
        }
        this.availableSingleSelectExtraGroups().forEach((extraGroup: MenuExtraGroupData) => {
            let value: number = 0;
            extraGroup.extraProducts.every((extraProduct: MenuExtraProductData) => {
                if (extraProduct.defaultOrderQty) {
                    value = extraProduct.price;
                    return false;
                }
                return true;
            });
            map.set(extraGroup.id, value);
        });
        return map;
    });

    public readonly basePriceForGroup: Signal<(extraId: number) => number> = computed(() => {
        const groupBasePriceMap: Map<number, number> = this.groupBasePriceMap();
        return (extraId: number) => groupBasePriceMap.get(extraId);
    });

    protected readonly _qtyOrderedPerSellableId: Signal<Map<number, number>> = computed(() => {
        const map: Map<number, number> = new Map();
        if (this._sellable()) {
            map.set(this._sellable().sellableId, this._quantitySignal());
        }
        this.orderedExtrasSignal().forEach((orderExtraItem: OrderExtraItem) =>
            map.set(orderExtraItem.extraProduct.productLine.sellableId, (map.get(orderExtraItem.extraProduct.productLine.sellableId) ?? 0) + (orderExtraItem.quantitySignal() * this._quantitySignal())));
        return map;
    });
    public readonly qtyOrderedPerSellableId: Signal<Map<number, number>> = computed(() => this._qtyOrderedPerSellableId());
    public readonly sellableQtyOrdered: Signal<(sellableId: number) => number> = computed(() => {
        const qtyOrderedPerSellableId: Map<number, number> = this.qtyOrderedPerSellableId();
        return (sellableId: number) => qtyOrderedPerSellableId.get(sellableId) ?? 0;
    });

    protected readonly childrenAtLimit: Signal<(qtyToAdd: number, excludedQty?: (sellableId: number) => number) => boolean> = computed(() => {
        const sellableAtLimit: (sellableId: number, quantum?: number, qtyPending?: number, excludedQty?: number) => boolean = OrderModel.instance.sellableAtLimit();
        const orderedExtras: OrderExtraItem[] = this.orderedExtrasSignal();

        return (qtyToAdd: number, excludedQty?: (sellableId: number) => number): boolean =>
            orderedExtras.some((extraItem: OrderExtraItem) => {
                const sellableId: number = extraItem.extraProduct.menuItem ? extraItem.extraProduct.menuItem.sellable.sellableId : extraItem.extraProduct.productLine.sellableId;
                const extraExcludedQty: number = excludedQty ? excludedQty(sellableId) : 0;
                return sellableAtLimit(sellableId, qtyToAdd * extraItem.quantitySignal(), 0, extraExcludedQty);
            });
    });

    public readonly isAtLimit: Signal<(quantum?: number, qtyPending?: number, excludedQty?: (sellableId: number) => number) => boolean> = computed(() => {
        const isOrderValid: boolean = OrderModel.instance.isOrderValid();
        const qtyAvailable: number = MenuModel.instance.sellableQtyAvailable()(this._sellable()?.sellableId);
        const sellableId: number = this._menuItem()?.sellable.sellableId ?? this._sellable()?.sellableId;
        const qtyOrdered: number = OrderModel.instance.qtyOrderedPerSellableId().get(sellableId) ?? 0;
        const childrenAtLimit: (qtyToAdd: number, excludedQty?: (sellableId: number) => number) => boolean = this.childrenAtLimit();

        return (this._sellable() && qtyAvailable != undefined && isOrderValid)
            ? (quantum: number = 1, qtyPending: number = 0, excludedQty?: (sellableId: number) => number): boolean =>
                (qtyOrdered - (excludedQty ? excludedQty(sellableId) : 0) + quantum + qtyPending > qtyAvailable) || childrenAtLimit(quantum + qtyPending, excludedQty)
            : (quantum: number = 1, qtyPending: number = 0, excludedQty?: (sellableId: number) => number): boolean =>
                childrenAtLimit(quantum + qtyPending, excludedQty);
    });

    private readonly availableSingleSelectExtraGroups: Signal<MenuExtraGroupData[]> = computed(() =>
        this._sellable()?.extraGroups.filter((extraGroup: MenuExtraGroupData) => MenuModel.instance.extraGroupIsSingleSelect(extraGroup)) ?? []);
    private readonly availableMultiSelectExtraGroups: Signal<MenuExtraGroupData[]> = computed(() =>
        this._sellable()?.extraGroups.filter((extraGroup: MenuExtraGroupData) => !MenuModel.instance.extraGroupIsSingleSelect(extraGroup)) ?? []);

    /**
     * Maps the ordered extras to an extra group id + sellable id
     */
    private readonly extraMapByExtraSellableId: WritableSignal<Map<string, OrderExtraItem>> = signal<Map<string, OrderExtraItem>>(new Map());
    public readonly orderedExtraForExtraSellableId: Signal<(extraId: number, sellableId: number) => OrderExtraItem> = computed(() => {
        const extraMapByExtraSellableId: Map<string, OrderExtraItem> = this.extraMapByExtraSellableId();
        return (extraId: number, sellableId: number) => extraMapByExtraSellableId.get(extraId + "_" + sellableId);
    });

    public readonly availableExtraGroups: Signal<MenuExtraGroupData[]> = computed(() => this._sellable()?.extraGroups ?? []);

    protected readonly _selectedFilterTags: Signal<string> = computed(() => this.orderedExtrasSignal().reduce((tags: string, extraItem: OrderExtraItem) =>
        tags + (extraItem.extraProduct.menuItem?.tagItemOnOptionSelection && extraItem.extraProduct.menuItem?.filterTags ? " " + extraItem.extraProduct.menuItem.filterTags : ""), ""));
    public readonly selectedFilterTags: Signal<string> = computed(() => this._selectedFilterTags());

    public readonly optionalOrderedExtrasForGroups: Signal<(extraGroups: MenuExtraGroupData[]) => OrderExtraItem[]> = computed(() => {
        const orderedExtras: OrderExtraItem[] = this.orderedExtrasSignal();
        const sellable: MenuSellableData = this._sellable();
        return (extraGroups: MenuExtraGroupData[]) => {
            return orderedExtras.filter((orderedExtra: OrderExtraItem) => {
                if (!extraGroups.includes(orderedExtra.extraGroup)) {
                    return false;
                }
                const parentExtraGroupId: number = MenuModel.instance.getParentExtraGroupId(orderedExtra.extraGroup, sellable);
                return parentExtraGroupId
                    ? extraGroups
                        .filter((extraGroup: MenuExtraGroupData) => !MenuModel.instance.getExtraGroupMinQty(extraGroup))
                        .map((extraGroup: MenuExtraGroupData) => extraGroup.id)
                        .includes(parentExtraGroupId)
                    : !MenuModel.instance.getExtraGroupMinQty(orderedExtra.extraGroup);
            });
        }
    });

    public readonly isExtraGroupBelowQuantityMinimum: Signal<(extraGroup: MenuExtraGroupData) => boolean> = computed(() => {
        const orderedExtras: OrderExtraItem[] = this.orderedExtrasSignal();
        // track all extras quantities
        orderedExtras.forEach((orderedExtra: OrderExtraItem) => orderedExtra.quantitySignal());
        return (extraGroup: MenuExtraGroupData) => MenuModel.instance.getExtraGroupMinQty(extraGroup) && orderedExtras
            .filter((orderedExtra: OrderExtraItem) => orderedExtra.extraGroup.id === extraGroup.id)
            .reduce((total, orderedExtra) => total + orderedExtra.quantitySignal(), 0) < MenuModel.instance.getExtraGroupMinQty(extraGroup);
    });

    public readonly extraGroupCount: Signal<(extraGroup: MenuExtraGroupData) => number> = computed(() => {
        const orderedExtras: OrderExtraItem[] = this.orderedExtrasSignal();
        orderedExtras.forEach((orderedExtra: OrderExtraItem) => orderedExtra.quantitySignal());
        return (extraGroup: MenuExtraGroupData) => orderedExtras
            .filter((orderExtraItem) => orderExtraItem.extraGroup.id === extraGroup.id)
            .reduce((total: number, orderExtraItem: OrderExtraItem) => total + orderExtraItem.quantitySignal(), 0);
    });

    public readonly hasExtraGroupReachedMin: Signal<(extraGroup: MenuExtraGroupData) => boolean> = computed(() => {
        const extraGroupCount: (extraGroup: MenuExtraGroupData) => number = this.extraGroupCount();
        return (extraGroup: MenuExtraGroupData) => (MenuModel.instance.getExtraGroupMinQty(extraGroup) ? extraGroupCount(extraGroup) <= MenuModel.instance.getExtraGroupMinQty(extraGroup) : false);
    });

    public readonly hasExtraGroupReachedLimit: Signal<(extraGroup: MenuExtraGroupData) => boolean> = computed(() => {
        const extraGroupCount: (extraGroup: MenuExtraGroupData) => number = this.extraGroupCount();
        return (extraGroup: MenuExtraGroupData) => {
            const groupQtyLimit: number = MenuModel.instance.getExtraGroupMaxQty(extraGroup);
            return groupQtyLimit != null && extraGroupCount(extraGroup) >= groupQtyLimit;
        }
    });

    public readonly extraGroupsBelowQuantityMinimum: Signal<MenuExtraGroupData[]> = computed(() =>
        this._sellable()?.extraGroups.filter((extraGroup: MenuExtraGroupData) =>
            MenuModel.instance.getExtraGroupMinQty(extraGroup) && this.isExtraGroupConditionallyAvailable(extraGroup) && this.isExtraGroupBelowQuantityMinimum()(extraGroup)));

    public readonly validationMessage: Signal<MessageDetails | undefined> = computed(() => {
        if (!this._sellable()) {
            return undefined;
        }

        if (this.extraGroupsBelowQuantityMinimum().length > 0) {
            const extraMissing: MenuExtraGroupData = this.extraGroupsBelowQuantityMinimum()[0];
            return new MessageDetails(MessageLevel.ERROR, "woa.message.order.minExtraQtyRequired", {
                minQty: MenuModel.instance.getExtraGroupMinQty(extraMissing),
                item: MenuModel.instance.getExtraGroupDisplayName(extraMissing)
            });
        }

        if (this.sellable().decomposedProductLine && (!this.parent || this.parent.sellable()?.decomposedProductLine) && !this.orderedExtrasSignal().length) {
            return new MessageDetails(MessageLevel.ERROR, "woa.message.order.extraSelectionRequired", {
                item: this.sellable().name
            });
        }

        return undefined;
    });

    public readonly isComplete: Signal<boolean> = computed(() => this.validationMessage() == undefined);

    constructor(public parent: OrderCompositeItem,
                menuItem: MenuItemData,
                sellable: MenuSellableData,
                name: string,
                quantity: number,
                dealId?: string,
                public readonly transferRequestId?: string,
                public readonly transferRequestName?: string,
                itemNotes?: string,
                proposed: boolean = false) {
        super();
        this._menuItem.set(menuItem);
        this._sellable.set(sellable);
        this.nameSignal.set(name);
        this._quantitySignal.set(quantity || 1);
        this._debouncedQuantitySignal.set(quantity || 1);
        this.dealIdSignal.set(dealId);
        this.itemNotesSignal.set(itemNotes);
        this.proposed.set(proposed);
    }

    public addToList(orderItems: WritableSignal<OrderItem[]>, index?: number): void {
        if (index === undefined) {
            for (let i: number = orderItems().length - 1; i >= 0; i--) {
                if (orderItems()[i].sellable().sellableId === this._sellable()?.sellableId) {
                    index = i + 1;
                    break;
                }
            }
        }
        orderItems.update(items => index === undefined ? items.concat(this) : items.toSpliced(index, 0, this));
        this.added.set(true);
    }

    /**
     * Add all default extras for the item.
     * For extras with assigned menu items, the available qty (if specified) is enforced.
     * For calculation of the quantity already used in the order, this order item (and its parent composite item if any)
     * is/are assumed not to have been added to the order at this time.
     *
     * @param orderCompositeItem  If the item is part of a composite - used for checking available quantities - otherwise this order item is used.
     * @param limitToProducts  If specified, only check default for products in this list.
     * @param excludeExtraGroups  If specified, do not add defaults for extra groups in this list.
     * @return true if any changes are made, false otherwise
     */
    public addDefaultExtras(orderCompositeItem?: OrderCompositeItem, limitToProducts?: MenuExtraProductData[], excludeExtraGroups?: MenuExtraGroupData[]): boolean {
        let qtyOrderedPerSellableId: Map<number, number> = undefined;
        let changed: boolean = false;
        this.availableExtraGroups().forEach((extraGroup: MenuExtraGroupData) => extraGroup.extraProducts.forEach((extraProduct: MenuExtraProductData) => {
            if (excludeExtraGroups && excludeExtraGroups.some((excludedGroup: MenuExtraGroupData) => excludedGroup.id === extraGroup.id)) {
                return;
            }
            if (extraProduct.defaultOrderQty &&
                (!limitToProducts || limitToProducts.indexOf(extraProduct) >= 0) &&
                this.orderedExtrasSignal().find((orderedExtra: OrderExtraItem) => orderedExtra.extraProduct === extraProduct) == undefined &&
                this.isExtraProductAvailable(extraProduct, orderCompositeItem ? orderCompositeItem._menuItem() : this._menuItem()) &&
                this.isExtraGroupConditionallyAvailable(extraGroup)) {
                let qtyToOrder: number = extraProduct.defaultOrderQty;

                // Don't add defaults in excess of the group limit
                const groupQtyLimit: number = MenuModel.instance.getExtraGroupMaxQty(extraGroup);
                if (groupQtyLimit) {
                    qtyToOrder = Math.min(qtyToOrder, Math.max(groupQtyLimit - this.extraGroupCount()(extraGroup), 0));
                }

                // Check available quantity
                const qtyAvailable: number = MenuModel.instance.sellableQtyAvailable()(extraProduct.productLine.sellableId);
                if (extraProduct.productLine && qtyAvailable != undefined) {

                    // Determine current quantities of all products in the order
                    if (!qtyOrderedPerSellableId) {
                        qtyOrderedPerSellableId = new Map(OrderModel.instance.qtyOrderedPerSellableId());
                        (orderCompositeItem ?? this).qtyOrderedPerSellableId().forEach((qty: number, sellableId: number) =>
                            qtyOrderedPerSellableId.set(sellableId, (qtyOrderedPerSellableId.get(sellableId) ?? 0) + qty * this._quantitySignal()));
                    }
                    const qtyOrdered = qtyOrderedPerSellableId.get(extraProduct.productLine.sellableId) ?? 0;

                    qtyToOrder = Math.min(qtyToOrder, qtyAvailable - qtyOrdered);
                }

                if (qtyToOrder > 0) {
                    let orderExtraItem: OrderExtraItem = this.createOrderExtraItem(extraGroup, extraProduct, qtyToOrder);
                    this.addOrderExtraItem(orderExtraItem);
                    changed = true;
                }
            }
        }));
        if (changed && this.added()) {
            OrderModel.instance.notifyOrderChange();
        }
        return changed;
    }

    /**
     * Determines whether the given extra product is available generally, and specifically for this order item and the specified menu item.
     * Existing filter tags from existing options on this order item are applied to filter conditional extras.
     *
     * NOTE: this references signals but is not reactive: Do not use in computed signals or templates.
     *
     * @param extraProduct  The extra product to check
     * @param productMenuItem  The menu item being used to order the product. Used for extras filtering. For composites, this should be the composite product's menu item rather than that of a component.
     */
    public isExtraProductAvailable(extraProduct: MenuExtraProductData, productMenuItem: MenuItemData): boolean {
        if (extraProduct && extraProduct.productLine && extraProduct.productLine.suppressForDecomposedProductLine && this._sellable()?.decomposedProductLine) {
            return false;
        }
        return MenuModel.instance.extraItemIsAvailable()(extraProduct ? extraProduct.menuItem : undefined, productMenuItem, this.selectedFilterTags());
    }

    /**
     * Determines whether the given extra group is conditionally available.
     *
     * NOTE: this references signals but is not reactive: Do not use in computed signals or templates.
     */
    public isExtraGroupConditionallyAvailable(extraGroup: MenuExtraGroupData): boolean {
        return !MenuModel.instance.getParentExtraGroupId(extraGroup, this._sellable()) ||
            this.orderedExtrasSignal().some((extraItem: OrderExtraItem) => extraGroup.parentExtraProductIds.indexOf(extraItem.extraProduct.id) >= 0);
    }

    protected clearOrderedExtras(): boolean {
        const changed: boolean = !!this.orderedExtrasSignal().length;
        this.orderedExtrasSignal().slice().map((extra: OrderExtraItem) => extra.identifier).forEach((identifier: string) => this.removeOrderExtraItem(identifier));
        return changed;
    }

    public createOrderExtraItem(extraGroup: MenuExtraGroupData, extraProductData: MenuExtraProductData, quantity?: number): OrderExtraItem {
        return new OrderExtraItem(this, extraGroup, extraProductData, quantity);
    }

    public addOrderExtraItem(orderExtraItem: OrderExtraItem, notify: boolean = false): void {
        const sortingExtraGroupId: number = orderExtraItem.isNestedSignal() ? MenuModel.instance.getParentExtraGroupId(orderExtraItem.extraGroup, this._sellable()) : orderExtraItem.extraGroup.id;
        orderExtraItem.sortingExtraGroupIndex = this._sellable()?.extraGroups.findIndex((extraGroup: MenuExtraGroupData) => extraGroup.id === sortingExtraGroupId);
        this.orderedExtrasSignal.update(extras => extras.concat(orderExtraItem));
        this.sortExtraItems();

        this.extraMapByExtraSellableId.update(map =>
            new Map(map).set(orderExtraItem.extraGroup.id + "_" + orderExtraItem.extraProduct.productLine.sellableId, orderExtraItem));
        if (notify) {
            OrderModel.instance.notifyOrderChange();
        }
    }

    public removeOrderExtraItem(identifier: string, notify: boolean = false): boolean {
        let index: number = this.orderedExtrasSignal().findIndex((extra: OrderExtraItem) => extra.identifier === identifier);
        if (index >= 0) {
            this.extraMapByExtraSellableId.update(map => {
                map = new Map(map);
                map.delete(this.orderedExtrasSignal()[index].extraGroup.id + "_" + this.orderedExtrasSignal()[index].extraProduct.productLine.sellableId);
                return map;
            });
            this.orderedExtrasSignal.update(extras => extras.toSpliced(index, 1));
            this.sortExtraItems();
            if (notify) {
                OrderModel.instance.notifyOrderChange();
            }
            return true;
        }
        return false;
    }

    /**
     * Sort extras by the order of the extra group, and product within the group as received from the server.
     * Nested extras are sorted within their parent group, directly after the first extra product that references their group.
     */
    private sortExtraItems() {
        this.orderedExtrasSignal.update(extras => extras.toSorted((a: OrderExtraItem, b: OrderExtraItem) => {
            const sortingExtraProductIdA: string = a.isNestedSignal() ? (this.firstNestingProductId(a) + "." + a.extraGroup.id + "." + a.extraProduct.id) : a.extraProduct.id;
            const sortingExtraProductIdB: string = b.isNestedSignal() ? (this.firstNestingProductId(b) + "." + b.extraGroup.id + "." + b.extraProduct.id) : b.extraProduct.id;
            return a.sortingExtraGroupIndex !== b.sortingExtraGroupIndex ? a.sortingExtraGroupIndex - b.sortingExtraGroupIndex
                : sortingExtraProductIdA.localeCompare(sortingExtraProductIdB);
        }));
    }

    /**
     * Return the extra product id of the first ordered extra product which references the group of the specified extra item
     * @param orderExtraItem
     */
    private firstNestingProductId(orderExtraItem: OrderExtraItem): string {
        const includingExtraItem: OrderExtraItem = this.orderedExtrasSignal().find((item: OrderExtraItem) => orderExtraItem.extraGroup.parentExtraProductIds.indexOf(item.extraProduct.id) >= 0);
        return includingExtraItem ? includingExtraItem.extraProduct.id : "";
    }

    /**
     * Remove an order extra item from the order item by extra group and product
     * @param extraGroup
     * @param extraProduct
     * @param notify
     */
    public removeOrderExtraProduct(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData, notify: boolean = false): boolean {
        const orderedExtra: OrderExtraItem = this.orderedExtrasSignal().find((orderedExtra: OrderExtraItem) => orderedExtra.extraGroup === extraGroup && orderedExtra.extraProduct === extraProduct);
        return orderedExtra && this.removeOrderExtraItem(orderedExtra.identifier, notify);
    }

    public createClone(): OrderItem {
        const orderItem: OrderItem = new OrderItem(undefined, this._menuItem(), this._sellable(), this.nameSignal(), this.quantitySignal(), this.dealIdSignal(), this.transferRequestId, this.transferRequestName, this.itemNotesSignal(), false);
        this.cloneExtras(orderItem);
        OrderModel.instance.notifyOrderChange();
        return orderItem;
    }

    /**
     * Copy the extras from this order item to another order item
     *
     * @param orderItem
     * @protected
     */
    protected cloneExtras(orderItem: OrderItem): void {
        this.orderedExtrasSignal().forEach((orderExtraItem: OrderExtraItem) => orderItem.addOrderExtraItem(orderExtraItem.createClone(orderItem)));
    }

    /**
     * Copy the extras from another order item
     *
     * @param orderItem The order item to copy extras from
     * @param orderItemMenuItem Filters out unavailable extras
     * @param clean Remove existing multi-select extras?
     */
    public assignExtras(orderItem: OrderItem, orderItemMenuItem?: MenuItemData, clean: boolean = false): void {
        const applyOrderItem: OrderItem = orderItem instanceof OrderCompositeItem
            ? orderItem.compositeComponentsSignal().find((copyItem) => copyItem._sellable() && this._sellable() && copyItem._sellable().sellableId === this._sellable().sellableId)
            : orderItem;

        if (applyOrderItem) {
            let changed: boolean = clean && this.clearOrderedExtras();

            applyOrderItem.orderedExtrasSignal().forEach((item: OrderExtraItem) => {
                if (orderItemMenuItem && orderItemMenuItem.filterExtras) {
                    if (item.extraProduct.menuItem == undefined ||
                        !MenuModel.instance.extraItemIsAvailableForFilters(item.extraProduct.menuItem, orderItemMenuItem, applyOrderItem.selectedFilterTags())) {
                        return;
                    }
                }

                // For single select extra groups, delete all items before adding the copied one, otherwise delete any extra for the same sellable.
                if (!clean) {
                    this.orderedExtrasSignal().slice().forEach((orderedExtra: OrderExtraItem) => {
                        if (orderedExtra.extraGroup.id === item.extraGroup.id &&
                            (MenuModel.instance.extraGroupIsSingleSelect(item.extraGroup) || orderedExtra.extraProduct.productLine.sellableId === item.extraProduct.productLine.sellableId)) {
                            this.removeOrderExtraItem(orderedExtra.identifier);
                            changed = true;
                        }
                    });
                }

                if (this._sellable()?.extraGroups.find((extraGroup: MenuExtraGroupData) => extraGroup.id === item.extraGroup.id)) {
                    this.addOrderExtraItem(item.createClone(this));
                    changed = true;
                }
            });
            if (changed) {
                OrderModel.instance.notifyOrderChange();
            }
        }
    }
}

export class OrderCompositeItem extends OrderItem {

    public override readonly compositeComponentsSignal: WritableSignal<OrderItem[]> = signal<OrderItem[]>([]);

    public override readonly localTotalSignal: Signal<number> = computed(() =>
        this._localTotalSignal() + (this.compositeComponentsSignal() ? (this.quantitySignal() * (this.totalExtras() + this.totalPlg())) : 0));

    public override readonly debouncedLocalTotalSignal: Signal<number> = computed(() =>
        this._debouncedLocalTotalSignal() + (this.compositeComponentsSignal() ? (this._debouncedQuantitySignal() * (this.totalExtras() + this.totalPlg())) : 0));

    public override readonly totalExtras: Signal<number> = computed(() =>
        this.compositeComponentsSignal()
            .reduce((total: number, orderItem: OrderItem) => total + orderItem._totalExtras(), 0));

    private readonly totalPlg: Signal<number> = computed(() =>
        this.compositeComponentsSignal()
            .filter((orderItem: OrderItem) => orderItem instanceof OrderPlgItem)
            .reduce((total: number, orderItem: OrderPlgItem) => total + (orderItem.sellable()
                ? (orderItem.plg.sellableLines.find((plgSellable: MenuPlgSellableData) => plgSellable.sellable.sellableId === orderItem.sellable().sellableId).additionalPrice ?? 0)
                : 0), 0));

    public override qtyOrderedPerSellableId: Signal<Map<number, number>> = computed(() => {
        const map: Map<number, number> = this._qtyOrderedPerSellableId();
        this.compositeComponentsSignal().forEach((component: OrderItem) => {
            component.qtyOrderedPerSellableId().forEach((qty: number, sellableId: number) =>
                map.set(sellableId, (map.get(sellableId) ?? 0) + qty * this._quantitySignal()));
        });
        return map;
    });

    protected override readonly childrenAtLimit: Signal<(qtyToAdd: number, excludedQty?: (sellableId: number) => number) => boolean> = computed(() => {
        const compositeComponents: OrderItem[] = this.compositeComponentsSignal();
        return (qtyToAdd: number, excludedQty?: (sellableId: number) => number): boolean =>
            compositeComponents.some((component: OrderItem) =>
                component.isAtLimit()(qtyToAdd * component.quantitySignal(), 0, excludedQty));
    });

    public override readonly selectedFilterTags: Signal<string> = computed(() =>
        this._selectedFilterTags() + this.compositeComponentsSignal().reduce((tags: string, componentItem: OrderItem) =>
            tags + (componentItem instanceof OrderPlgItem && componentItem.menuItem()?.tagItemOnOptionSelection && componentItem.menuItem()?.filterTags ? " " + componentItem.menuItem().filterTags : ""), ""));

    public override readonly validationMessage: Signal<MessageDetails | undefined> = computed(() => {
        if (!this._sellable()) {
            return undefined;
        }

        return this.compositeComponentsSignal().map((component: OrderItem) => {
            if (component instanceof OrderPlgItem) {
                if (!component.plg.sellableLines.length) {
                    return undefined;
                }
                if (!component.sellable()) {
                    return new MessageDetails(MessageLevel.ERROR, "woa.heading.order.plgSelectionRequired", {
                        plgName: component.plg.externalName || component.plg.name
                    });
                }
            }
            return component.validationMessage();
        }).find(messageDetails => messageDetails !== undefined)
    });

    constructor(public readonly composite: MenuCompositeData,
                name: string,
                menuItem: MenuItemData,
                sellable: MenuSellableData,
                quantity: number,
                items: OrderItem[],
                transferRequestId?: string,
                transferRequestName?: string,
                itemNotes?: string,
                dealId?: string,
                proposed: boolean = false) {
        super(undefined, menuItem, sellable, name, quantity, dealId, transferRequestId, transferRequestName, itemNotes, proposed);
        items.forEach((item: OrderItem) => item.parent = this);
        this.compositeComponentsSignal.set(items);
    }

    public replaceOrderItem(identifier: string, orderItem: OrderItem): void {
        let index = this.compositeComponentsSignal().findIndex((item: OrderItem) => item.identifier === identifier);
        this.removeOrderItem(identifier);
        this.addOrderItem(orderItem, index);
        OrderModel.instance.notifyOrderChange();
    }

    protected addOrderItem(orderItem: OrderItem, index?: number) {
        orderItem.addToList(this.compositeComponentsSignal, index);
    }

    protected removeOrderItem(identifier: string): void {
        this.compositeComponentsSignal.update(items => items.toSpliced(items.findIndex((item: OrderItem) => item.identifier === identifier), 1));
    }

    public override createClone(): OrderCompositeItem {
        const orderItem: OrderCompositeItem = new OrderCompositeItem(this.composite, this.nameSignal(), this._menuItem(), this._sellable(), this.quantitySignal(), this.compositeComponentsSignal().map((orderItem: OrderItem) => orderItem.createClone()));
        orderItem.dealIdSignal.set(this.dealIdSignal());
        OrderModel.instance.notifyOrderChange();
        return orderItem;
    }

    /**
     * Copy the extras from another order item into this composite order item.
     * If the specified item is also a composite, it will copy the extras from each of its items into corresponding items in this composite order item.
     * If the specified item is not a composite, it will copy it into any item within this composite with the same sellable id.
     *
     * @param orderItem The order item to copy extras from
     * @param orderItemMenuItem Filters out unavailable extras
     */
    public override assignExtras(orderItem: OrderItem, orderItemMenuItem: MenuItemData): void {
        if (orderItem instanceof OrderCompositeItem) {
            // keep a list of items already applied, so when an order item can have the same sellable/plg multiple times
            const applied: OrderItem[] = [];
            orderItem.compositeComponentsSignal().forEach((copyItem: OrderItem) => {
                if (copyItem instanceof OrderPlgItem) {
                    if (copyItem.sellable()) {
                        const existingOrderPlgItem: OrderPlgItem = <OrderPlgItem>this.compositeComponentsSignal().find((existingItem: OrderItem) =>
                            existingItem instanceof OrderPlgItem &&
                            applied.find((appliedItem: OrderItem) => appliedItem === existingItem) == undefined &&
                            existingItem.plg.plgId === copyItem.plg.plgId);
                        if (existingOrderPlgItem) {
                            let canSelectSellable = true;
                            if (orderItemMenuItem.filterPlgItems) {
                                const plgSellable: MenuPlgSellableData =
                                    existingOrderPlgItem.plg.sellableLines.find((existingPlgSellable: MenuPlgSellableData) => existingPlgSellable.sellable.sellableId === copyItem.sellable().sellableId);
                                if (plgSellable == undefined || plgSellable.menuItem == undefined ||
                                    !MenuModel.instance.plgItemIsAvailableForFilters(plgSellable.menuItem, orderItemMenuItem)) {
                                    canSelectSellable = false;
                                }
                            }
                            if (canSelectSellable) {
                                existingOrderPlgItem.selectSellable(copyItem.sellable(), orderItem);
                                existingOrderPlgItem.assignExtras(copyItem, orderItemMenuItem);
                                applied.push(existingOrderPlgItem);
                            }
                        }
                    }
                } else {
                    const existingOrderItem: OrderItem = this.compositeComponentsSignal().find((existingItem) => applied.find((appliedItem: OrderItem) => appliedItem === existingItem) == undefined
                        && existingItem.sellable() && copyItem.sellable() && existingItem.sellable().sellableId === copyItem.sellable().sellableId);
                    if (existingOrderItem) {
                        existingOrderItem.assignExtras(copyItem, orderItemMenuItem);
                        applied.push(existingOrderItem);
                    }
                }
            });
        } else {
            const existingOrderItem: OrderItem = this.compositeComponentsSignal().find((existingItem) =>
                existingItem.sellable() && orderItem.sellable() && existingItem.sellable().sellableId === orderItem.sellable().sellableId);
            if (existingOrderItem) {
                existingOrderItem.assignExtras(orderItem, orderItemMenuItem);
            }
        }
    }
}

export class OrderPlgItem extends OrderItem {

    public readonly matchesSellable: Signal<(plgSellable: MenuPlgSellableData) => boolean> = computed(() => {
        const sellable: MenuSellableData = this._sellable();
        return (plgSellable: MenuPlgSellableData): boolean => sellable && plgSellable.sellable && plgSellable.sellable.sellableId === sellable.sellableId;
    });

    public override readonly isComplete: Signal<boolean> = computed(() => this._sellable() && this.validationMessage() == undefined);

    constructor(parent: OrderCompositeItem,
                public readonly plg: MenuPlgData,
                name: string,
                sellable: MenuSellableData,
                quantity: number,
                dealId?: string,
                transferRequestId?: string,
                transferRequestName?: string) {
        super(parent, sellable ? sellable.menuItem : undefined, sellable, name, quantity, dealId, transferRequestId, transferRequestName, undefined, false);
    }

    public selectSellable(sellable: MenuSellableData, orderCompositeItem: OrderCompositeItem): void {
        if (!this._sellable() || this._sellable().sellableId !== sellable.sellableId) {
            this._sellable.set(sellable);
            this._menuItem.set(sellable.menuItem);
            this.nameSignal.set(MenuModel.instance.getMenuItemDisplayName(sellable.menuItem) || sellable.name);

            this.clearOrderedExtras();
            this.addDefaultExtras(orderCompositeItem);
            OrderModel.instance.notifyOrderChange();
        }
    }

    public override createClone(): OrderPlgItem {
        const orderItem: OrderPlgItem = new OrderPlgItem(undefined, this.plg, this.nameSignal(), this._sellable(), this.quantitySignal(), this.dealIdSignal());
        this.cloneExtras(orderItem);
        OrderModel.instance.notifyOrderChange();
        return orderItem;
    }
}
